import type { ScaleBand } from 'd3-scale';
import { isUndefined } from 'lodash-es';

import { useCategoricalBarChartScales } from './useCategoricalBarChartScales.js';
import { useCategoricalBarFilteredData } from './useCategoricalBarFilteredData.js';
import { useClosestDimensionPoints } from './useClosestDimensionPoints.js';
import type { Point } from '../../core/types/point.js';
import type { DimensionXYPoint } from '../types/categorical-bar-chart-data.js';
import type {
  CategoricalValueScales,
  GroupMode,
  Layout,
} from '../types/categorical-bar-chart.js';
import { findClosestPointInGroupedChart } from '../utils/groupedDistancePoints.js';
import { isStacked } from '../utils/layout.js';
import { findClosestPointInStackedChart } from '../utils/stackedDistancePoints.js';

/**
 * Retrieve the dimension X/Y points based on the
 * closest category considering the threshold on each chart
 *
 * @param layout - layout mode of the chart
 * @param groupMode - group mode of the chart
 * @param mousePosition - mouse coordinate
 * @returns closest point and closes points of the mouse coordinate
 */
export const useClosestDimensionInThresholdPoints = (
  layout: Layout,
  groupMode: GroupMode = 'stacked',
  mousePosition?: Point,
) => {
  const { isSingleDimension } = useCategoricalBarFilteredData();
  const { categoryScale, valueScale, dimensionScale } =
    useCategoricalBarChartScales();

  const closestDimensionPoints = useClosestDimensionPoints(
    layout,
    groupMode,
    mousePosition,
  );

  if (isUndefined(mousePosition)) {
    return;
  }

  return findClosestDimensionPointInThreshold(
    layout,
    mousePosition,
    closestDimensionPoints,
    groupMode,
    categoryScale,
    valueScale,
    dimensionScale,
    isSingleDimension,
  );
};

const findClosestDimensionPointInThreshold = (
  layout: Layout,
  mousePosition: Point,
  closestDimensionPoints: DimensionXYPoint[],
  groupMode: GroupMode,
  categoryScale: ScaleBand<string>,
  valueScale: CategoricalValueScales,
  dimensionScale: ScaleBand<string>,
  isSingleDimension: boolean,
): DimensionXYPoint | undefined => {
  if (mousePosition && closestDimensionPoints.length > 0) {
    let closestDimensionPointInThreshold: DimensionXYPoint | undefined;
    if (isStacked(groupMode) || isSingleDimension) {
      closestDimensionPointInThreshold = findClosestPointInStackedChart(
        layout,
        mousePosition,
        closestDimensionPoints,
        valueScale,
        categoryScale,
      );
    } else {
      closestDimensionPointInThreshold = findClosestPointInGroupedChart(
        layout,
        mousePosition,
        closestDimensionPoints,
        categoryScale,
        dimensionScale,
      );
    }

    return closestDimensionPointInThreshold;
  }

  return undefined;
};
